import React from 'react'
import { css } from '@emotion/core'
import { H1, H2, Paragraph, Separator, WhiteTheme, colors } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import { useSurname } from '../../hooks/useSurname/useSurname'
import { processHtmlDefinition } from '../../helpers/processHtmlDefinition/processHtmlDefinition'
import SurnameCountries from '../../components/SurnameCountries/SurnameCountries'
import PlaceholderSurnamePage from '../PlaceholderSurnamePage/PlaceholderSurnamePage'
import calculatePageParagraphs from '../../helpers/calculatePageParagraphs/calculatePageParagraphs'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../helpers/getHasDarkBackground'
import useIsSafari from '../../hooks/useIsSafari/useIsSafari'

const surnameHeaderCss = (gender, hasDarkBackground) => {
  const isFemale = gender === 'FEMALE'
  const lightColor = isFemale ? '#FEBBC4' : '#84DAED'
  const darkColor = isFemale ? '#8B4450' : '#2C5A6B'

  return css`
    background-color: ${hasDarkBackground ? darkColor : lightColor};
    padding: 8px;
  `
}

const countryBorderCss = css`
  padding: 10px;
`

const similarNamesCss = (hasDarkBackground) => css`
  float: left;
  width: 130px;
  margin-inline-end: 8px;
  padding: 5px;
  background-color: ${hasDarkBackground ? '#616161' : '#dddfdf'};
  border-radius: 4px;
`

const wrapWithTheme = (hasDarkBackground, content) => (hasDarkBackground ? <WhiteTheme>{content}</WhiteTheme> : content)

const meaningSectionCss = css`
  overflow: hidden; /* Creates new block formatting context */
  height: 100%;
`

const loadingContainerCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  flex-direction: column;
  gap: 16px;
`

const errorContainerCss = (hasDarkBackground) => css`
  text-align: center;
  padding: 20px;
  background-color: ${hasDarkBackground ? colors.gray80 : '#fef2f2'};
  border: 1px solid ${hasDarkBackground ? colors.gray60 : '#fecaca'};
  border-radius: 8px;
  margin: 20px 0;
`

const fontCSS = (isSafari) => css`
  & p {
    font-family: 'Noto Sans CJK SC', 'Source Han Sans SC', serif !important;
  }

  @media print {
    & p {
      font-size: ${isSafari ? '13pt' : '15pt'} !important;
    }
  }
`

const pageWrapperCSS = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto 1fr;
  height: 100%;
  width: 100%;
  gap: 10px;

  @media print {
    height: auto;
  }
`

export default function GlobalSurnamePage({
  surname,
  surnamePage = '1',
  showMap = true,
  isPrintView = false,
  pageNumber,
}) {
  const [t] = useTranslation()
  const isSafari = useIsSafari()
  const { surnameData, loading, error } = useSurname(surname)
  const { gender } = useUser()

  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)
  const renderWithTheme = (content) => wrapWithTheme(hasDarkBackground, content)

  if (!surname) {
    return <PlaceholderSurnamePage pageNumber={pageNumber} isGlobalSurnamePage />
  }

  if (loading && !surnameData) {
    return (
      <div css={pageWrapperCSS}>
        <div css={loadingContainerCss}>{renderWithTheme(<NoticeLoading size="md" />)}</div>
      </div>
    )
  }

  if (error) {
    const errorBody = (
      <>
        <H2 size="H5">{t('GlobalSurnamePage.error.loadingSurnameInfo', 'Unable to load surname information')}</H2>
        <Paragraph size="sm">
          {t(
            'GlobalSurnamePage.error.loadingSurnameInfoDescription',
            'There was an error loading the information. Please try again later.'
          )}
        </Paragraph>
      </>
    )

    return (
      <div css={pageWrapperCSS}>
        <div css={errorContainerCss(hasDarkBackground)}>{renderWithTheme(errorBody)}</div>
      </div>
    )
  }

  // Get variants from surname data
  const surnameHeadingLabel =
    typeof surname === 'string' && surname.length > 0
      ? surname.charAt(0).toUpperCase() + surname.slice(1).toLowerCase()
      : surname
  const variants = surnameData?.variants?.VARIANT || []

  const arrayParagraphs = processHtmlDefinition(surnameData?.htmlDefinition)
  const { paragraphsPageOne, remainingParagraphs } = calculatePageParagraphs(arrayParagraphs)
  const paragraphsToShow = surnamePage === '1' ? paragraphsPageOne : remainingParagraphs

  const hasParagraphs = paragraphsToShow.length > 0
  const isEmptyData = !hasParagraphs && variants.length === 0

  if (isEmptyData) {
    const noDataContent = (
      <>
        <H1 size="H5" centered>
          {t('GlobalSurnamePage.noData.title', 'Surname Information Unavailable')}
        </H1>
        {isPrintView ? <div style={{ height: '30px' }} /> : <Separator />}
        <Paragraph size="sm">
          {t(
            'GlobalSurnamePage.noData.description',
            `We're sorry, but we don't have historical or geographical information available for the surname {surnameHeading}.`,
            { surnameHeading: surnameHeadingLabel }
          )}
        </Paragraph>
      </>
    )

    return <div css={pageWrapperCSS}>{renderWithTheme(noDataContent)}</div>
  }

  const meaningHeadingKey = showMap ? 'GlobalSurnamePage.meaning.label' : 'GlobalSurnamePage.meaningContinued.label'
  const meaningHeadingDefault = showMap ? 'Meaning:' : 'Meaning: (Continued)'

  const content = (
    <div css={pageWrapperCSS}>
      <div css={surnameHeaderCss(gender, hasDarkBackground)}>
        <H1 size="H3" centered>
          {surnameHeadingLabel}
        </H1>
      </div>

      {showMap && !isEmptyData && (
        <div css={countryBorderCss}>
          <SurnameCountries
            surname={surnameHeadingLabel}
            countries={surnameData?.countries || []}
            wrapMaps={!hasParagraphs}
          />
        </div>
      )}

      <div css={meaningSectionCss}>
        {showMap && variants.length > 0 && (
          <div css={similarNamesCss(hasDarkBackground)}>
            <Paragraph size="sm">{t('GlobalSurnamePage.similarNames.label', 'Similar Names:')}</Paragraph>
            {variants.map((variant) => (
              <Paragraph size="xs" key={`variant-${variant.name}`}>
                {variant.name}
              </Paragraph>
            ))}
          </div>
        )}

        {hasParagraphs && <H2 size="H5">{t(meaningHeadingKey, meaningHeadingDefault)}</H2>}
        {isPrintView ? <div style={{ height: '5px' }} /> : <Separator size="nano" />}
        {paragraphsToShow.map?.((paragraph) => (
          <div css={fontCSS(isSafari)} key={paragraph}>
            <Paragraph
              size="xs"
              dangerouslySetInnerHTML={{ __html: paragraph }}
              data-testid="surname-definition-text"
              dense
            />
          </div>
        ))}
      </div>
    </div>
  )

  return <div>{renderWithTheme(content)}</div>
}
